import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Typography,
  Divider,
  Tooltip,
  Dropdown,
  Upload,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  SendOutlined,
  EyeOutlined,
  CopyOutlined,
  ExportOutlined,
  ImportOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import RichTextEditor, { RichTextEditorRef } from '../components/RichTextEditor';
import notificationService, { Notification } from '../services/notification';
import useDocumentTitle from '../hooks/useDocumentTitle';
import userPreferenceService from '../services/userPreference';
import { getApiResponseErrorMessage, getAxiosErrorMessage } from '../utils/error';
import { getColumnTitle } from '../config/columns';
import { TRADEMARK_VARIABLES, ORDER_VARIABLES, PATENT_PAYMENT_VARIABLES } from '../config/notification-variables';
import { NATION_OPTIONS } from '../config/nations';
import ListHelpButton from '../components/ListHelpButton';

const { Option } = Select;
const { Text } = Typography;

// Helper function to decode HTML entities
const decodeHtmlEntities = (html: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
};

const NotificationManagement: React.FC = () => {
  useDocumentTitle('通知模板 - 设置');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [testSendVisible, setTestSendVisible] = useState(false);
  const [renderPreview, setRenderPreview] = useState<{ subject?: string; body?: string } | null>(null);
  const [wechatTestVisible, setWechatTestVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [previewNotification, setPreviewNotification] = useState<Notification | null>(null);
  const [testSendNotification, setTestSendNotification] = useState<Notification | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [testSendForm] = Form.useForm();
  const [wechatTestForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [notifTypeFilter, setNotifTypeFilter] = useState<string | undefined>(undefined);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string | undefined>(undefined);
  const [nationFilter, setNationFilter] = useState<string | undefined>(undefined);
  const richTextEditorRef = useRef<RichTextEditorRef>(null);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 10 });
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [testSendLoading, setTestSendLoading] = useState(false);
  const [nationOptions, setNationOptions] = useState<{ value: string; label: string }[]>([]);
  const handleResetFilters = () => {
    setSearchText('');
    setNotifTypeFilter(undefined);
    setServiceTypeFilter(undefined);
    setNationFilter(undefined);
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const blob = await notificationService.exportNotifications({
        search: searchText || undefined,
        nation: nationFilter,
        service_type: serviceTypeFilter,
        notification_type: notifTypeFilter,
      } as any);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `通知模板数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };


  // 导入数据
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await notificationService.importNotifications(file);
      setImportProgress(100);

      if (result.success) {
        const { total = 0, success = 0, updated = 0, failed = 0, errors = [] } = result.data || {};
        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;

        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
          if (errors && errors.length > 0) {
            messageText += `\n错误详情: ${errors.slice(0, 3).join('; ')}`;
            if (errors.length > 3) {
              messageText += '...';
            }
          }
        }

        if (failed === 0) {
          message.success(messageText);
        } else {
          message.warning(messageText);
        }

        fetchNotifications();
      } else {
        message.error(`导入失败: ${result.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }
  };

  // 状态选项（通用/默认）
  const statusOptions = [
    { value: 'submitted', label: '已提交', color: 'blue' },
    { value: 'reviewing', label: '审查中', color: 'orange' },
    { value: 'approved', label: '已通过', color: 'green' },
    { value: 'rejected', label: '已驳回', color: 'red' },
    { value: 'registered', label: '已注册', color: 'purple' },
    { value: 'expired', label: '即将过期', color: 'volcano' },
    { value: 'renewed', label: '已续费', color: 'cyan' },
  ];

  // 业务类型选项
  const serviceTypeOptions = [
    { value: 'trademark', label: '商标服务' },
    { value: 'patent', label: '专利服务' },
    { value: 'copyright', label: '版权服务' },
    { value: 'company', label: '工商服务' },
    { value: 'barcode', label: '条件码服务' },
    { value: 'other', label: '其他服务' },
  ];

  // 通知类型选项（radio）
  const notificationTypeOptions = [
    { value: 'trademark', label: '商标监控通知' },
    { value: 'order', label: '订单过期通知' },
    { value: 'payment', label: '专利缴费通知' },
  ];

  const templateTypeOptions = [
    { value: 'email', label: '邮件' },
    { value: 'wechat', label: '微信' },
  ];

  // 监听通知类型，自动设置/清理业务类型
  const watchedNotificationType = Form.useWatch('notification_type', form);
  // 监听模板类型，用于条件显示渠道字段
  const watchedTemplateType = Form.useWatch('template_type', form);
  // 监听邮件正文，用于显式控制 RichTextEditor
  const bodyValue = Form.useWatch('body', form);
  React.useEffect(() => {
    // 只在新建模式下自动设置 service_type，编辑模式下保留原值
    if (!editingNotification) {
      if (watchedNotificationType === 'trademark') {
        form.setFieldsValue({ service_type: 'trademark' });
      } else if (watchedNotificationType === 'payment') {
        form.setFieldsValue({ service_type: 'patent' });
      } else if (watchedNotificationType === 'order') {
        // 不自动设置，由用户选择
        form.setFieldsValue({ service_type: undefined });
      }
    }
  }, [watchedNotificationType, editingNotification]);

  React.useEffect(() => {
    if (watchedTemplateType === 'wechat' && !editingNotification) {
      form.setFieldsValue({
        subject: undefined,
        wechat_template_id: undefined,
      });
    }
  }, [watchedTemplateType, editingNotification, form]);

  // Helper function to generate variables from keys
  const generateVariables = (keys: string[]) => {
    return keys.map(key => ({
      key: `[${key}]`,
      desc: getColumnTitle(key) || key,
    }));
  };

  // 从配置文件导入变量定义，自动生成带描述的变量
  const trademarkVariables = generateVariables(TRADEMARK_VARIABLES);
  const orderVariables = generateVariables(ORDER_VARIABLES);
  const patentPaymentVariables = generateVariables(PATENT_PAYMENT_VARIABLES);

  // 获取通知数据
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications({
        search: searchText || undefined,
        notification_type: notifTypeFilter,
        service_type: serviceTypeFilter,
        nation: nationFilter,
        per_page: 200,
      } as any);
      if (response.success) {
        // 兼容分页或非分页响应
        const list = (response.data as any).data || response.data;
        setNotifications(list);
      } else {
        message.error('获取通知模板失败');
      }
    } catch (error) {
      console.error('获取通知模板失败:', error);
      message.error('获取通知模板失败');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchNotifications();
  }, [searchText, notifTypeFilter, serviceTypeFilter, nationFilter]);

  // 加载国家列表
  useEffect(() => {
    const loadNations = async () => {
      try {
        const response = await notificationService.getNations();
        if (response.success && Array.isArray(response.data)) {
          setNationOptions(response.data.map(n => ({ value: n, label: n })));
        }
      } catch (error) {
        console.error('加载国家列表失败:', error);
      }
    };
    loadNations();
  }, [notifications]); // 当通知列表变化时重新加载国家列表

  // 记住/应用每页条数
  useEffect(() => {
    const loadPageSize = async () => {
      try {
        const saved = await userPreferenceService.getPageSizeSettings('notifications');
        if (saved && saved > 0 && saved !== pagination.pageSize) {
          setPagination(prev => ({ ...prev, pageSize: saved, current: 1 }));
        }
      } catch { }
    };
    loadPageSize();
  }, []);

  // 过滤条件变更时重置到第一页
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [searchText, notifTypeFilter, serviceTypeFilter, nationFilter]);

  // 表格列定义
  // 针对“专利缴费通知”的状态集合
  const paymentStatusOptions = [
    { value: 'patent_started', label: '已开始', color: 'blue' },
    { value: 'patent_due_over', label: '已过宽限期', color: 'orange' },
    { value: 'patent_grace_over', label: '已过期', color: 'red' },
  ];

  // 获取状态展示文案（列表用）
  const getStatusDisplay = (status: string, record?: Notification) => {
    if (record?.notification_type === 'payment') {
      const m = paymentStatusOptions.find(o => o.value === status);
      return { label: m?.label || status, color: m?.color };
    }
    const m = statusOptions.find(o => o.value === status);
    return { label: m?.label || status, color: m?.color };
  };

  const columns: ColumnsType<Notification> = [
    {
      title: '模板主题',
      dataIndex: 'subject',
      key: 'subject',
      width: 350,
      render: (subject: string, record: Notification) => (
        <Button
          type="link"
          onClick={() => handlePreview(record)}
          style={{ padding: 0, textAlign: 'left' }}
        >
          {subject || (record.template_type === 'wechat' ? '微信模板' : '-')}
        </Button>
      ),
    },
    {
      title: '模板类型',
      dataIndex: 'template_type',
      key: 'template_type',
      width: 80,
      render: (templateType?: 'email' | 'wechat') => (
        <Tag color={templateType === 'wechat' ? 'green' : 'blue'}>
          {templateTypeOptions.find(opt => opt.value === (templateType || 'email'))?.label || '邮件'}
        </Tag>
      ),
    },
    {
      title: '触发状态',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (status: string, record: Notification) => {
        const { label, color } = getStatusDisplay(status, record);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '业务类型',
      dataIndex: 'service_type',
      key: 'service_type',
      width: 60,
      render: (type: string) => {
        const option = serviceTypeOptions.find(opt => opt.value === type);
        return <Tag color="blue">{option?.label || type}</Tag>;
      },
    },
    {
      title: '通知类型',
      dataIndex: 'notification_type',
      key: 'notification_type',
      width: 60,
      render: (type: string) => {
        const option = notificationTypeOptions.find(opt => opt.value === type);
        return <Tag>{option?.label || type || '-'}</Tag>;
      },
    },
    {
      title: '启用通知',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 60,
      render: (enabled: boolean, record: Notification) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggleEnabled(record.id, checked)}
          size="small"
        />
      ),
    },
    {
      title: '发送给客户',
      dataIndex: 'send_to_customer',
      key: 'send_to_customer',
      width: 80,
      render: (sendToCustomer: boolean | undefined, record: Notification) => (
        <Switch
          checked={sendToCustomer ?? true}
          onChange={(checked) => handleToggleSendToCustomer(record.id, checked)}
          size="small"
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record: Notification) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
            title="预览"
          />
          <Button
            icon={<SendOutlined />}
            onClick={() => (record.template_type === 'wechat' ? handleWechatTest(record) : handleTestSend(record))}
            size="small"
            title={record.template_type === 'wechat' ? '测试微信' : '测试邮件'}
            style={record.template_type === 'wechat' ? { color: '#52c41a' } : undefined}
          />
          <Button
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record)}
            size="small"
            title="复制"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            title="编辑"
          />
          <Popconfirm
            title="确定删除这个通知模板吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              title="删除"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 预览模板
  const handlePreview = (notification: Notification) => {
    setPreviewNotification(notification);
    setPreviewVisible(true);
  };

  // 测试发送 - 打开测试发送模态框
  const handleTestSend = (notification: Notification) => {
    setTestSendNotification(notification);
    testSendForm.setFieldsValue({
      test_email: '', // 清空邮箱输入
    });
    setRenderPreview(null);
    setTestSendVisible(true);
  };

  // 执行测试发送
  const handleConfirmTestSend = async () => {
    try {
      const values = await testSendForm.validateFields();

      if (!testSendNotification) {
        message.error('未找到要测试的通知模板');
        return;
      }

      if (!values.data_id) {
        message.error('请输入数据ID进行测试');
        return;
      }

      const testData = {
        status: testSendNotification.status || '',
        service_type: testSendNotification.service_type || '',
        notification_type: (testSendNotification as any).notification_type as 'trademark' | 'order' | 'payment',
        data_id: Number(values.data_id),
        test_email: values.test_email,
      };

      setTestSendLoading(true);
      const response = await notificationService.testSendEmail(testData);

      if (response.success) {
        message.success(`测试邮件已发送至 ${values.test_email}`);
        setTestSendVisible(false);
      } else {
        message.error(getApiResponseErrorMessage(response, '测试邮件发送失败'));
      }
    } catch (error: any) {
      console.error('测试发送失败:', error);
      message.error(getAxiosErrorMessage(error, '测试发送失败'));
    } finally {
      setTestSendLoading(false);
    }
  };

  // 微信测试 - 打开微信测试模态框
  const handleWechatTest = (notification: Notification) => {
    if (!notification.wechat_template_id) {
      message.error('请先配置微信模板ID');
      return;
    }

    setTestSendNotification(notification);
    wechatTestForm.setFieldsValue({
      wechat_openid: '', // 清空微信OpenID输入
    });
    setWechatTestVisible(true);
  };

  // 执行微信测试发送
  const handleConfirmWechatTest = async () => {
    try {
      const values = await wechatTestForm.validateFields();

      if (!testSendNotification) {
        message.error('未找到要测试的通知模板');
        return;
      }

      if (!testSendNotification.wechat_template_id) {
        message.error('微信模板ID不能为空');
        return;
      }

      const testData = {
        subject: testSendNotification.subject || '',
        body: testSendNotification.body || '',
        wechat_openid: values.wechat_openid,
        wechat_template_id: testSendNotification.wechat_template_id,
        service_type: testSendNotification.service_type,
        status: testSendNotification.status,
        notification_type: (testSendNotification as any).notification_type,
        data_id: values.data_id ? Number(values.data_id) : undefined,
      };

      const response = await notificationService.testWechatSend(testData);

      if (response.success) {
        message.success(`微信测试消息已发送`);
        setWechatTestVisible(false);
      } else {
        message.error(getApiResponseErrorMessage(response, '微信测试消息发送失败'));
      }
    } catch (error: any) {
      console.error('微信测试发送失败:', error);
      message.error(getAxiosErrorMessage(error, '微信测试发送失败'));
    }
  };


  // 复制模板
  const handleCopy = async (notification: Notification) => {
    try {
      const { id, ...notificationWithoutId } = notification;
      const newNotificationData = {
        ...notificationWithoutId,
        subject: notification.subject ? `${notification.subject} (副本)` : undefined,
      };

      // 先创建新记录到数据库
      const response = await notificationService.createNotification(newNotificationData);

      if (response.success && response.data) {
        message.success('模板复制成功');
        // 刷新列表
        await fetchNotifications();
        // 打开编辑模态框编辑新创建的记录
        handleEdit(response.data);
      } else {
        message.error(getApiResponseErrorMessage(response, '复制失败'));
      }
    } catch (error: any) {
      console.error('复制失败:', error);
      message.error(getAxiosErrorMessage(error, '复制失败'));
    }
  };

  // 切换启用状态
  const handleToggleEnabled = async (id: number | undefined, enabled: boolean) => {
    if (!id) {
      message.error('无效的模板ID');
      return;
    }

    try {
      const response = await notificationService.updateNotification(id, { enabled });
      if (response.success) {
        message.success(enabled ? '模板已启用' : '模板已禁用');
        fetchNotifications();
      } else {
        message.error(getApiResponseErrorMessage(response, '状态更新失败'));
      }
    } catch (error) {
      console.error('状态更新失败:', error);
      message.error('状态更新失败');
    }
  };

  // 切换客户通知状态
  const handleToggleSendToCustomer = async (id: number | undefined, send_to_customer: boolean) => {
    if (!id) {
      message.error('无效的模板ID');
      return;
    }

    try {
      const response = await notificationService.updateNotification(id, { send_to_customer });
      if (response.success) {
        message.success(send_to_customer ? '已允许发送给客户' : '已关闭客户通知');
        fetchNotifications();
      } else {
        message.error(getApiResponseErrorMessage(response, '客户通知设置更新失败'));
      }
    } catch (error) {
      console.error('客户通知设置更新失败:', error);
      message.error('客户通知设置更新失败');
    }
  };

  // 处理新增/编辑
  const handleEdit = (notification?: Notification) => {
    setEditingNotification(notification || null);
    if (notification) {
      form.setFieldsValue(notification);
    } else {
      form.resetFields();
      form.setFieldsValue({
        template_type: 'email',
        enabled: true,
        send_to_customer: true,
      });
    }
    setModalVisible(true);
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingNotification) {
        // 更新现有通知模板
        const response = await notificationService.updateNotification(editingNotification.id!, values);
        if (response.success) {
          message.success('通知模板更新成功');
        } else {
          message.error(getApiResponseErrorMessage(response, '更新失败'));
          return;
        }
      } else {
        // 创建新的通知模板
        const response = await notificationService.createNotification(values);
        if (response.success) {
          message.success('通知模板添加成功');
        } else {
          message.error(getApiResponseErrorMessage(response, '创建失败'));
          return;
        }
      }

      setModalVisible(false);
      fetchNotifications();
    } catch (error: any) {
      console.error('保存失败:', error);
      message.error(getAxiosErrorMessage(error, '保存失败'));
    }
  };

  // 处理删除
  const handleDelete = async (id: number | undefined) => {
    if (!id) {
      message.error('无效的模板ID');
      return;
    }

    try {
      const response = await notificationService.deleteNotification(id);
      if (response.success) {
        message.success('通知模板删除成功');
        fetchNotifications();
      } else {
        message.error(getApiResponseErrorMessage(response, '删除失败'));
      }
    } catch (error: any) {
      console.error('删除失败:', error);
      message.error(getAxiosErrorMessage(error, '删除失败'));
    }
  };

  // 过滤数据
  const filteredNotifications = notifications.filter(notification =>
    !searchText ||
    notification.subject?.toLowerCase().includes(searchText.toLowerCase()) ||
    notification.body?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={(
          <Space size="small">
            <span>通知模板</span>
            <ListHelpButton page_key="notification_management" />
          </Space>
        )}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleEdit()}
            >
              新建模板
            </Button>
            <Dropdown
              menu={{
                items: [
                  { key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport },
                  { key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) },
                ]
              }}
            >
              <Button data-testid="btn-notify-data-menu">数据管理</Button>
            </Dropdown>

            {selectedRowKeys.length > 0 && (
              <Popconfirm
              title={`确定删除选中的 ${selectedRowKeys.length} 个模板吗？`}
              okText="确定"
              cancelText="取消"
              onConfirm={async () => {
                const ids = selectedRowKeys as number[];
                try {
                  const response = await notificationService.batchDeleteNotifications(ids);
                  if (response.success) {
                    message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 个通知模板`);
                    setSelectedRowKeys([]);
                    fetchNotifications();
                  } else {
                    message.error(response.message || '批量删除失败');
                  }
                } catch (e: any) {
                  message.error(e?.response?.data?.message || '批量删除失败');
                }
              }}
              disabled={!selectedRowKeys.length}
            >
              <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
            </Popconfirm>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Input
              placeholder="搜索模板名称、主题、内容"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col span={16} style={{ textAlign: 'left' }}>
            <Space>
              <Select
                allowClear
                showSearch
                placeholder="通知类型"
                style={{ width: 160, textAlign: 'left' }}
                value={notifTypeFilter}
                onChange={setNotifTypeFilter}
              >
                {notificationTypeOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>

              {/* 业务类型在选择了商标/专利缴费类型时可隐藏 */}
              {!(notifTypeFilter === 'trademark' || notifTypeFilter === 'payment') && (
                <Select
                  allowClear
                  showSearch
                  placeholder="业务类型"
                  style={{ width: 160, textAlign: 'left' }}
                  value={serviceTypeFilter}
                  onChange={setServiceTypeFilter}
                >
                  {serviceTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              )}

              {/* 国家筛选 */}
              <Select
                allowClear
                showSearch
                placeholder="国家"
                style={{ width: 180, textAlign: 'left' }}
                value={nationFilter}
                onChange={setNationFilter}
                optionFilterProp="children"
              >
                {nationOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>

              <Button onClick={() => { setNotifTypeFilter(undefined); setServiceTypeFilter(undefined); setNationFilter(undefined); setSearchText(''); }}>重置</Button>
            </Space>
          </Col>
        </Row>

        {(searchText || notifTypeFilter || serviceTypeFilter || nationFilter) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {searchText && (
                    <Tag closable onClose={() => setSearchText('')}>
                      搜索: {searchText}
                    </Tag>
                  )}
                  {notifTypeFilter && (
                    <Tag closable onClose={() => setNotifTypeFilter(undefined)}>
                      通知类型: {notificationTypeOptions.find(opt => opt.value === notifTypeFilter)?.label || notifTypeFilter}
                    </Tag>
                  )}
                  {serviceTypeFilter && (
                    <Tag closable onClose={() => setServiceTypeFilter(undefined)}>
                      业务类型: {serviceTypeOptions.find(opt => opt.value === serviceTypeFilter)?.label || serviceTypeFilter}
                    </Tag>
                  )}
                  {nationFilter && (
                    <Tag closable onClose={() => setNationFilter(undefined)}>
                      国家: {nationOptions.find(opt => opt.value === nationFilter)?.label || nationFilter}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={filteredNotifications}
          loading={loading}
          scroll={{ x: 1760 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredNotifications.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: async (page, pageSize) => {
              if (pageSize !== pagination.pageSize) {
                try { await userPreferenceService.savePageSizeSettings('notifications', pageSize); } catch { }
              }
              setPagination({ current: page, pageSize });
            }
          }}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys as any, preserveSelectedRowKeys: true }}
          rowKey="id"
        />
      </Card>

      {/* 导入模态框 */}
      <Modal
        title="导入通知模板"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportProgress(0);
        }}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Upload.Dragger
            name="file"
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              handleImport(file);
              return false;
            }}
            showUploadList={false}
            disabled={importLoading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">支持 .xlsx, .xls, .csv 格式文件，单个文件不超过10MB</p>
          </Upload.Dragger>

          {importLoading && (
            <div style={{ marginTop: 20 }}>
              <Progress percent={importProgress} />
              <p>导入中，请稍候...</p>
            </div>
          )}

          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              <li>请使用模板整理数据；邮件模板需填写主题，微信模板可留空主题</li>
              <li>通知类型支持 trademark/order/payment；业务类型可选</li>
              <li>同主题+通知类型+状态重复时会更新原记录</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title={editingNotification ? '编辑通知模板' : '新建通知模板'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={1200}
        destroyOnClose
        key={editingNotification?.id || 'new'}
      >
        <Form
          form={form}
          layout="vertical"
        >

          {/* 第一行：通知类型 | 模板类型 | 触发状态 */}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="通知类型"
                name="notification_type"
                rules={[{ required: true, message: '请选择通知类型' }]}
              >
                <Select placeholder="请选择通知类型">
                  {notificationTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="模板类型"
                name="template_type"
                rules={[{ required: true, message: '请选择模板类型' }]}
              >
                <Select placeholder="请选择模板类型">
                  {templateTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                label="触发状态"
                name="status"
                rules={[{ required: true, message: '请输入触发状态' }]}
                help="时间类型的通知，请输入：-30,-10 (天数)"
              >
                <Input placeholder="请输入触发状态" />
              </Form.Item>
            </Col>

            {watchedNotificationType === 'trademark' && (
              <Col span={6}>
                <Form.Item
                  label="国家"
                  name="nation"
                >
                  <Select
                    placeholder="请选择国家"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {NATION_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {!(watchedNotificationType === 'trademark' || watchedNotificationType === 'payment') && (
              <Col span={6}>
                <Form.Item
                  label="业务类型"
                  name="service_type"
                  rules={[{ required: true, message: '请选择业务类型' }]}
                >
                  <Select placeholder="请选择业务类型">
                    {serviceTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          {watchedTemplateType !== 'wechat' && (
            <Form.Item
              label="邮件主题"
              name="subject"
              rules={[{ required: true, message: '请输入邮件主题' }]}
            >
              <Input placeholder="请输入邮件主题，可使用模板变量" />
            </Form.Item>
          )}

          <Form.Item
            label="邮件正文"
            name="body"
            rules={[{ required: true, message: '请输入邮件正文' }]}
            help={watchedTemplateType === 'wechat' ? '微信模板请按“{{thing2.DATA}}:[application_no]”格式逐行填写映射关系' : undefined}
          >
            <RichTextEditor
              ref={richTextEditorRef}
              value={bodyValue}
              onChange={(content) => form.setFieldValue('body', content)}
              placeholder={watchedTemplateType === 'wechat' ? '请输入微信模板字段映射，例如 {{thing2.DATA}}:[application_no]' : '请输入邮件正文，可使用模板变量和富文本格式'}
            />
          </Form.Item>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Form.Item label="启用通知" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="发送通知给客户" name="send_to_customer" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>

            {(editingNotification || watchedTemplateType !== 'wechat') && (
              <Col span={8}>
                <Form.Item
                  label="微信模板ID"
                  name="wechat_template_id"
                  help="编辑好模板提交给微信审核，审核通过后，把模板ID填入这里"
                >
                  <Input placeholder="请输入微信模板消息ID" />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 模板变量说明：根据通知类型显示对应变量集 */}
          <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
            <div style={{ marginBottom: 16, fontWeight: 600, color: '#262626' }}>
              可用的模板变量（点击复制后粘贴到邮件内容中）：
            </div>
            {!watchedNotificationType && (
              <div style={{ color: '#999', marginBottom: 8 }}>请选择通知类型以查看对应可用变量</div>
            )}

            {watchedNotificationType === 'trademark' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {trademarkVariables.map((variable) => (
                    <Tooltip key={variable.key} title={variable.desc}>
                      <code
                        style={{
                          background: '#f5f5f5',
                          padding: '4px 8px',
                          borderRadius: 3,
                          fontFamily: 'Monaco, Consolas, monospace',
                          color: '#1890ff',
                          cursor: 'pointer',
                          fontSize: '12px',
                          border: '1px solid #e8e8e8'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(variable.key);
                          message.success(`已复制: ${variable.key}`);
                        }}
                      >
                        {variable.key}
                      </code>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            {watchedNotificationType === 'order' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {orderVariables.map((variable) => (
                    <Tooltip key={variable.key} title={variable.desc}>
                      <code
                        style={{
                          background: '#f5f5f5',
                          padding: '4px 8px',
                          borderRadius: 3,
                          fontFamily: 'Monaco, Consolas, monospace',
                          color: '#52c41a',
                          cursor: 'pointer',
                          fontSize: '12px',
                          border: '1px solid #e8e8e8'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(variable.key);
                          message.success(`已复制: ${variable.key}`);
                        }}
                      >
                        {variable.key}
                      </code>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            {watchedNotificationType === 'payment' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                  {patentPaymentVariables.map((variable) => (
                    <Tooltip key={variable.key} title={variable.desc}>
                      <code
                        style={{
                          background: '#f5f5f5',
                          padding: '4px 8px',
                          borderRadius: 3,
                          fontFamily: 'Monaco, Consolas, monospace',
                          color: '#cf1322',
                          cursor: 'pointer',
                          fontSize: '12px',
                          border: '1px solid #e8e8e8'
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(variable.key);
                          message.success(`已复制: ${variable.key}`);
                        }}
                      >
                        {variable.key}
                      </code>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Form>
      </Modal>

      {/* 预览模态框 */}
      <Modal
        title="模板预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {previewNotification && (
          <div>
            {previewNotification.subject && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>邮件主题：</Text>
                  <div style={{ marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                    {previewNotification.subject}
                  </div>
                </div>

                <Divider />
              </>
            )}

            <div>
              <Text strong>邮件正文：</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 4,
                  minHeight: 200,
                  border: '1px solid #d9d9d9'
                }}
                dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(previewNotification.body || '') }}
              />
            </div>

            <div style={{ marginTop: 16, fontSize: '12px', color: '#999' }}>
              <Text type="secondary">
                注：实际发送时，模板变量会被替换为真实数据
              </Text>
            </div>
          </div>
        )}
      </Modal>

      {/* 测试发送模态框 */}
      <Modal
        title="测试发送邮件"
        open={testSendVisible}
        onOk={handleConfirmTestSend}
        onCancel={() => setTestSendVisible(false)}
        width={600}
        okText={testSendLoading ? '发送中...' : '发送测试邮件'}
        cancelText="取消"
        confirmLoading={testSendLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            将向指定邮箱发送此邮件模板的测试邮件，模板变量会被替换为测试数据
          </Text>
        </div>

        {testSendNotification && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <div><strong>主题：</strong>{testSendNotification.subject || '-'}</div>
            {!(testSendNotification as any).notification_type || !(['trademark', 'payment'].includes((testSendNotification as any).notification_type as any)) ? (
              <div><strong>业务类型：</strong>{serviceTypeOptions.find(opt => opt.value === testSendNotification.service_type)?.label}</div>
            ) : null}
            <div><strong>触发状态：</strong>{(() => { const r = getStatusDisplay(testSendNotification.status || '', testSendNotification as any); return r.label; })()}</div>
          </div>
        )}

        <Form
          form={testSendForm}
          layout="vertical"
          onValuesChange={async (changed, all) => {
            if ('data_id' in changed) {
              const id = Number(changed.data_id);
              if (testSendNotification?.notification_type && id) {
                try {
                  const resp = await notificationService.testRender({
                    subject: testSendNotification.subject || '',
                    body: testSendNotification.body || '',
                    notification_type: testSendNotification.notification_type as any,
                    data_id: id,
                    service_type: testSendNotification.service_type,
                    status: testSendNotification.status,
                  });
                  if (resp.success) {
                    setRenderPreview(resp.data || null);
                  } else {
                    setRenderPreview(null);
                  }
                } catch (e) {
                  setRenderPreview(null);
                }
              } else {
                setRenderPreview(null);
              }
            }
          }}
        >
          <Form.Item label="数据ID" name="data_id" rules={[{ required: true, message: '请输入数据ID' }]} tooltip="根据通知类型从对应数据表读取数据填充模板：商标监控=trademark，异常订单=orders，专利年费监控=patent_payments">
            <Input placeholder="请输入对应数据表的ID，如 123" />
          </Form.Item>
          <Form.Item
            label="测试邮箱"
            name="test_email"
            rules={[
              { required: true, message: '请输入测试邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入用于测试的邮箱地址" />
          </Form.Item>
        </Form>

        {renderPreview && (
          <div style={{ marginTop: 16 }}>
            <Divider orientation="left">渲染预览</Divider>
            <div style={{ marginBottom: 8 }}>
              <Text strong>主题：</Text> {renderPreview.subject}
            </div>
            <div
              style={{
                padding: 12,
                backgroundColor: '#f5f5f5',
                borderRadius: 4,
                minHeight: 120,
                border: '1px solid #d9d9d9'
              }}
              dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(renderPreview.body || '') }}
            />
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: '12px', color: '#999' }}>
          <Text type="secondary">
            注：测试邮件中的模板变量（如 {'[name]'}, {'[trademark_name]'} 等）会被替换为示例数据
          </Text>
        </div>
      </Modal>

      {/* 微信测试发送模态框 */}
      <Modal
        title="测试发送微信消息"
        open={wechatTestVisible}
        onOk={handleConfirmWechatTest}
        onCancel={() => setWechatTestVisible(false)}
        width={600}
        okText="发送微信测试消息"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            将向指定微信用户发送此模板的测试消息，模板变量会被替换为测试数据
          </Text>
        </div>

        {testSendNotification && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <div><strong>主题：</strong>{testSendNotification.subject || '-'}</div>
            <div><strong>微信模板ID：</strong>{testSendNotification.wechat_template_id}</div>
            {!(testSendNotification as any).notification_type || !(['trademark', 'payment'].includes((testSendNotification as any).notification_type as any)) ? (
              <div><strong>业务类型：</strong>{serviceTypeOptions.find(opt => opt.value === testSendNotification.service_type)?.label}</div>
            ) : null}
            <div><strong>触发状态：</strong>{(() => { const r = getStatusDisplay(testSendNotification.status || '', testSendNotification as any); return r.label; })()}</div>
          </div>
        )}

        <Form
          form={wechatTestForm}
          layout="vertical"
        >
          <Form.Item label="数据ID（可选）" name="data_id" tooltip="根据通知类型从对应数据表读取数据填充模板：商标监控=trademark，异常订单=orders，专利年费监控=patent_payments">
            <Input placeholder="请输入对应数据表的ID，如 123" />
          </Form.Item>
          <Form.Item
            label="测试微信OpenID"
            name="wechat_openid"
            rules={[
              { required: true, message: '请输入测试微信OpenID' }
            ]}
          >
            <Input placeholder="请输入用于测试的微信OpenID" />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 16, fontSize: '12px', color: '#999' }}>
          <Text type="secondary">
            注：需要先关注公众号的微信用户才能接收模板消息
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default NotificationManagement;
